import React, { useState, useMemo } from 'react';
import { DREAMS_DATABASE, DREAM_CATEGORIES, ARABIC_LETTERS } from '../data/dreamsData';
import { DICTIONARY_DB, DictionaryEntry } from '../data/dictionaryDb';

interface DictionaryViewProps {
  onSelectDream: (slug: string) => void;
  onSelectLetter: (letter: string) => void;
  onSelectCategory: (categorySlug: string) => void;
  onStartAiInterpretation: (symbolName?: string) => void;
  onBackToHome: () => void;
}

const preventDefault = (e: React.MouseEvent, handler: () => void) => {
  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
    e.preventDefault();
    handler();
  }
};

const PAGE_SIZE = 60;

const DictionaryView: React.FC<DictionaryViewProps> = ({
  onSelectDream,
  onSelectLetter,
  onSelectCategory,
  onStartAiInterpretation,
  onBackToHome
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [page, setPage] = useState(1);

  const searchMode = searchQuery.trim() !== '';

  const featuredDreamSlugs = useMemo(() => new Set(DREAMS_DATABASE.map(d => d.slug)), []);

  const canonicalRedirectMap = useMemo(() => {
    const m = new Map<string, string>();
    DICTIONARY_DB.forEach(e => {
      if (e.canonicalSlug) m.set(e.slug, e.canonicalSlug);
    });
    return m;
  }, []);

  const filteredFeatured = DREAMS_DATABASE.filter(dream => {
    const matchesSearch = !searchMode ||
      dream.name.includes(searchQuery) ||
      dream.summary.includes(searchQuery) ||
      dream.keywords.some(k => k.includes(searchQuery));
    const matchesCategory = selectedCategory === 'all' || dream.categorySlug === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredDictionaryAll = DICTIONARY_DB.filter((entry) => {
    if (entry.canonicalSlug && featuredDreamSlugs.has(entry.canonicalSlug)) return false;
    if (featuredDreamSlugs.has(entry.slug)) return false;
    const matchesSearch = !searchMode ||
      entry.name.includes(searchQuery) ||
      entry.text.includes(searchQuery) ||
      entry.category.includes(searchQuery) ||
      entry.slug.includes(searchQuery);
    const matchesCategory = selectedCategory === 'all' || entry.categorySlug === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalSymbols = filteredFeatured.length + filteredDictionaryAll.length;
  const totalPages = Math.max(1, Math.ceil((Math.max(0, filteredDictionaryAll.length - (searchMode ? 0 : 0))) / PAGE_SIZE));

  const dictSlice = searchMode
    ? filteredDictionaryAll.slice(0, 120)
    : filteredDictionaryAll.slice(0, page * PAGE_SIZE);

  const dictHasMore = !searchMode && dictSlice.length < filteredDictionaryAll.length;

  const catCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    DREAM_CATEGORIES.forEach(c => { counts[c.slug] = 0; });
    DREAMS_DATABASE.forEach(d => {
      counts.all++;
      if (counts[d.categorySlug] !== undefined) counts[d.categorySlug]++;
    });
    DICTIONARY_DB.forEach(e => {
      if (e.canonicalSlug && featuredDreamSlugs.has(e.canonicalSlug)) return;
      if (featuredDreamSlugs.has(e.slug)) return;
      counts.all++;
      if (counts[e.categorySlug] !== undefined) counts[e.categorySlug]++;
    });
    return counts;
  }, [featuredDreamSlugs]);

  const resetFilters = () => {
    setPage(1);
  };

  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-slate-100 py-8 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Navigation Breadcrumb */}
        <nav aria-label="مسار التصفح" className="flex items-center gap-2 text-sm text-slate-300 mb-6 pb-4 border-b border-slate-700/60">
          <a href="/" onClick={(e) => preventDefault(e, onBackToHome)} className="hover:text-emerald-400 transition-colors no-underline">الرئيسية</a>
          <span>/</span>
          <span className="text-emerald-400 font-semibold">قاموس تفسير الأحلام</span>
        </nav>

        {/* Header */}
        <header className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">
            قاموس وموسوعة تفسير الأحلام الشاملة
          </h1>
          <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto">
            تصفح دلالات رموز الرؤى والأحلام بالحروف الأبجدية والتصنيفات الموضوعية مستندة إلى مصادر التراث في علم التعبير.
          </p>
          <p className="text-slate-400 text-sm mt-3">
            ✨ إجمالي الرموز المتاحة: <span className="text-emerald-400 font-bold">{catCounts.all || (DREAMS_DATABASE.length + DICTIONARY_DB.length)}</span> رمزاً
          </p>
        </header>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="ابحث عن رمز في المنام (مثل: ثعبان، ذهب، موت، بحر، أسنان، أرنب، تابوت)..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); resetFilters(); }}
              className="w-full py-4 pr-12 pl-4 bg-slate-900/90 border-2 border-slate-700 focus:border-emerald-500 rounded-2xl text-white placeholder-slate-400 outline-none transition-all shadow-xl"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-slate-300">
              🔍
            </span>
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); resetFilters(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white text-xs bg-slate-800 px-2 py-1 rounded-md"
              >
                مسح
              </button>
            )}
          </div>
        </div>

        {/* Alphabet Bar */}
        <section className="mb-10">
          <h2 className="text-sm font-bold text-slate-300 mb-3 text-center uppercase tracking-wider">
            تصفح حسب الحرف الأبجدي
          </h2>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center max-w-4xl mx-auto">
            {ARABIC_LETTERS.map((letter) => (
              <a
                key={letter}
                href={`/letter/${encodeURIComponent(letter)}`}
                onClick={(e) => preventDefault(e, () => onSelectLetter(letter))}
                className="no-underline w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-slate-800/90 hover:bg-emerald-600 border border-slate-700 hover:border-emerald-500 text-white font-bold rounded-xl transition-all text-sm sm:text-base shadow-sm active:scale-95"
                title={`تصفح الرموز التي تبدأ بحرف ${letter}`}
              >
                {letter}
              </a>
            ))}
          </div>
        </section>

        {/* Category Filters */}
        <section className="mb-8">
          <h2 className="text-sm font-bold text-slate-300 mb-3 text-center uppercase tracking-wider">
            تصفح حسب التصنيف
          </h2>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => { setSelectedCategory('all'); resetFilters(); }}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                selectedCategory === 'all'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
              }`}
            >
              جميع الأقسام ({catCounts.all || 0})
            </button>
            {DREAM_CATEGORIES.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                onClick={(e) => preventDefault(e, () => { setSelectedCategory(cat.slug); resetFilters(); })}
                className={`no-underline px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                  selectedCategory === cat.slug
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.name}</span>
                <span className="text-[10px] opacity-70">({catCounts[cat.slug] || 0})</span>
              </a>
            ))}
          </div>
        </section>

        {/* Results Summary */}
        {(searchMode || selectedCategory !== 'all') && (
          <div className="mb-6 px-4 py-3 bg-slate-800/40 border border-slate-700/40 rounded-xl text-sm text-slate-300 flex flex-wrap items-center justify-between gap-2">
            <span>
              تم العثور على <span className="text-emerald-400 font-bold">{totalSymbols}</span> رمزاً
              {selectedCategory !== 'all' && (
                <> ضمن: <span className="text-emerald-400">{DREAM_CATEGORIES.find(c => c.slug === selectedCategory)?.name}</span></>
              )}
              {searchMode && (
                <> لبحث: <span className="text-amber-300">«{searchQuery}»</span></>
              )}
            </span>
            {(searchMode || selectedCategory !== 'all') && (
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('all'); resetFilters(); }}
                className="text-xs px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
              >
                إعادة تعيين الفلاتر
              </button>
            )}
          </div>
        )}

        {/* Results Grid */}
        <section className="mb-12">
          {totalSymbols > 0 ? (
            <>
              {/* Featured / Enhanced Dreams */}
              {filteredFeatured.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span>⭐</span>
                    <span>الرموز المحسنة والمفصلة (تفسير شامل)</span>
                    <span className="text-xs font-normal text-slate-400">({filteredFeatured.length} رمزاً)</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredFeatured.map((dream) => (
                      <a
                        key={dream.slug}
                        href={`/dream/${dream.slug}`}
                        onClick={(e) => preventDefault(e, () => onSelectDream(dream.slug))}
                        className="block no-underline bg-slate-800/60 border border-slate-700/70 hover:border-emerald-500 rounded-2xl p-6 transition-all hover:shadow-2xl hover:bg-slate-800/90 group cursor-pointer flex flex-col justify-between"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <span className="text-xs font-semibold bg-emerald-500/10 text-emerald-400 px-2.5 py-1 rounded-lg border border-emerald-500/20">
                              {dream.category}
                            </span>
                            <a
                              href={`/letter/${encodeURIComponent(dream.letter)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                preventDefault(e, () => onSelectLetter(dream.letter));
                              }}
                              className="no-underline text-xs text-slate-300 bg-slate-900 px-2 py-0.5 rounded border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors inline-block"
                            >
                              حرف ({dream.letter})
                            </a>
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                            {dream.title}
                          </h3>
                          <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3">
                            {dream.summary}
                          </p>
                        </div>
                        <div className="pt-4 border-t border-slate-700/60 flex items-center justify-between">
                          <span className="text-emerald-400 text-sm font-semibold group-hover:translate-x-[-4px] transition-transform">
                            قراءة التفسير بالكامل ←
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onStartAiInterpretation(dream.name);
                            }}
                            className="text-xs bg-slate-900 hover:bg-emerald-700 text-slate-300 hover:text-white px-2.5 py-1 rounded-lg border border-slate-700 transition-colors"
                          >
                            تفسير مخصص ✨
                          </button>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Dictionary entries — always shown (not only in search mode) */}
              {dictSlice.length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span>📖</span>
                    <span>
                      {searchMode ? 'نتائج الرموز من قاموس الأحلام' : 'بقية رموز قاموس الأحلام (المصدر التراثي)'}
                    </span>
                    <span className="text-xs font-normal text-slate-400">
                      ({searchMode ? `${dictSlice.length} / ${filteredDictionaryAll.length}` : `${dictSlice.length} من ${filteredDictionaryAll.length}`} رمزاً)
                    </span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dictSlice.map((entry: DictionaryEntry) => (
                      <a
                        key={entry.slug}
                        href={`/dream/${entry.slug}`}
                        onClick={(e) => preventDefault(e, () => onSelectDream(entry.slug))}
                        className="block no-underline bg-slate-800/40 border border-slate-700/50 hover:border-teal-500 rounded-xl p-4 transition-all cursor-pointer group flex flex-col justify-between gap-3"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <a
                              href={`/category/${entry.categorySlug}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                preventDefault(e, () => onSelectCategory(entry.categorySlug));
                              }}
                              className="no-underline text-[11px] text-teal-300 bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20 hover:bg-teal-500/20 hover:text-teal-200 transition-colors inline-block"
                            >
                              {entry.category}
                            </a>
                            <a
                              href={`/letter/${encodeURIComponent(entry.letter)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                preventDefault(e, () => onSelectLetter(entry.letter));
                              }}
                              className="no-underline text-[11px] text-slate-400 bg-slate-900 px-2 py-0.5 rounded border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors inline-block"
                            >
                              حرف ({entry.letter})
                            </a>
                          </div>
                          <h3 className="font-bold text-white group-hover:text-teal-300 transition-colors">{entry.name}</h3>
                          <p className="text-xs text-slate-400 leading-relaxed mt-1 line-clamp-2">{entry.text.slice(0, 140)}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onStartAiInterpretation(entry.name);
                          }}
                          className="text-[11px] text-slate-200 hover:text-emerald-300 bg-slate-900/80 px-2 py-0.5 rounded border border-slate-700 self-start"
                        >
                          تفسير مخصص بالذكاء الاصطناعي ✨
                        </button>
                      </a>
                    ))}
                  </div>

                  {dictHasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={() => setPage(p => p + 1)}
                        className="px-8 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold rounded-xl transition-all text-sm"
                      >
                        تحميل المزيد من الرموز ↓ ({filteredDictionaryAll.length - dictSlice.length} رمز متبقٍ)
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="bg-slate-800/40 border border-slate-700 rounded-3xl p-12 text-center max-w-md mx-auto">
              <span className="text-4xl mb-3 block">🔎</span>
              <h3 className="text-lg font-bold text-white mb-2">لم نعثر على نتائج مطابقة لـ "{searchQuery}"</h3>
              <p className="text-slate-300 text-sm mb-6">
                جرّب كلمة أخرى، أو اختر حرفاً من الفهرس أعلاه، أو اكتب تفاصيل حلمك ليقوم الذكاء الاصطناعي بتفسيرها.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => { setSearchQuery(''); setSelectedCategory('all'); resetFilters(); }}
                  className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all text-sm"
                >
                  عرض كل الرموز
                </button>
                <button
                  onClick={() => onStartAiInterpretation(searchQuery)}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg text-sm"
                >
                  فسر حلمك بالذكاء الاصطناعي ←
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Bottom Banner */}
        <section className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/30 rounded-3xl p-8 text-center max-w-3xl mx-auto shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-2">
            رؤيا مركبة بتفاصيل كثيرة؟
          </h2>
          <p className="text-slate-300 text-sm sm:text-base mb-6 max-w-xl mx-auto">
            اكتب تفاصيل حلمك كاملة وسيقوم الذكاء الاصطناعي بتحليل الرموز المركبة وربطها بظروفك الشخصية وفق منهج المفسرين التراثيين.
          </p>
          <p className="text-amber-300/80 text-xs mb-6 max-w-xl mx-auto">
            ⚠️ تفسير الذكاء الاصطناعي هو اجتهاد آلي ولا يُنسب مباشرة لابن سيرين أو النابلسي.
          </p>
          <button
            onClick={() => onStartAiInterpretation()}
            className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-2xl shadow-xl transition-all text-base active:scale-95"
          >
            ابدأ التفسير الفوري مجاناً ←
          </button>
        </section>
      </div>
    </div>
  );
};

export default DictionaryView;
